import { HTMLAttributes, ReactNode, startTransition, useEffect, useLayoutEffect, useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import styles from './VirtualTable.module.css';

export type VirtualTableType<T> = HTMLAttributes<HTMLDivElement> & {
  data: T[];
  // Initial height of the list.
  height: string | number;

  /** The component to render with the data, its first argument needs to be the object of the row to render */
  renderRow: (item: T) => ReactNode;
  /** Optional estimateSize defaults to () => 35 */
  estimateSize?: (index: number) => number;

  /** Render list with total size */
  collapse?: boolean;
};

const VirtualTable = <T,>({
  data,
  renderRow,
  collapse = undefined,
  estimateSize = () => 35,
  height = '',
  ...divProps
}: VirtualTableType<T>) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: data ? data.length : 0,
    getScrollElement: () => tableRef.current,
    estimateSize,
    overscan: 50,
    paddingEnd: 40,
  });

  useEffect(() => {
    startTransition(() => {
      if (collapse === true && tableRef.current) {
        tableRef?.current?.style.setProperty('height', `${virtualizer.getTotalSize()}px`);
        tableRef?.current?.style.setProperty('transition', `height 0.2s ease-in`);
      } else if (collapse === false) {
        tableRef?.current?.style.setProperty('height', typeof height === 'string' ? height : `${height}px`);
        tableRef?.current?.style.setProperty('transition', `height 0.2s ease-out`);
      } else {
        tableRef?.current?.style.setProperty('height', typeof height === 'string' ? height : `${height}px`);
      }
    });
  }, [collapse, data, virtualizer.getTotalSize()]);

  // Wordt benodigt voor dynamic recalculation of estimateSize: https://github.com/TanStack/virtual/discussions/632
  useLayoutEffect(() => {
    virtualizer?.measure?.();
  }, [data]);

  const classes = divProps?.className ? `${divProps.className} ` : '';

  return (
    <div ref={tableRef} {...divProps} className={`${classes}${styles.virtualTableStyle}`}>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: 'auto',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((row) => (
          <div
            key={row.key}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${row.start}px)`,
              height: `${row.size}px`,
            }}
          >
            {renderRow(data[row.index])}
          </div>
        ))}
      </div>
    </div>
  );
};
export default VirtualTable;
